import FaviconHead from '@components/head/favicons';
import HreflangHead from '@components/head/hreflangHead';
import SupportMetaTagHead from '@components/head/supportMetadata';
import { ModuleIllustration } from '@components/module/ModuleIllustration';
import { AI, COMMON, SECONDS_PER_DAY } from '@constants';
import {
    AiModuleFragment,
    ChatFragment,
    GetAiModuleByProductSlugAndLocaleDocument,
    GetAiModuleByProductSlugAndLocaleQuery,
} from '@graphql/generated/graphql';
import { Chat } from '@lib/pimster-ai-chat/components/Chat';
import { buildInitialMessage } from '@lib/pimster-ai-chat/utils/buildInitialMessage';
import { PageProps } from '@types';
import { buildHreflangArray } from '@utils/hreflang';
import { getModuleIllustration } from '@utils/module/illustration';
import {
    withApollo,
    withCompany,
    withErrorsGSP,
    withProduct,
} from '@utils/serverMethodPlugins';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

interface Props extends PageProps {
    AIModule: Omit<AiModuleFragment, 'Status' | 'localizations'>;
}

const SupportPage: NextPage<Props> = ({
    AIModule,
    hrefLang,
    serverCompanyContext,
    serverProductContext,
}) => {
    const {
        locale,
        query: { initialInput },
    } = useRouter();
    const chat = AIModule.Content?.[0] as ChatFragment | undefined;

    if (!chat) return null;

    return (
        <>
            <SupportMetaTagHead title={AIModule.Title} />
            <FaviconHead productMeta={serverProductContext.meta} />
            <HreflangHead
                refs={hrefLang}
                companySlug={serverCompanyContext.meta.Slug!}
                defaultLocale={serverCompanyContext.meta.DefaultLocale!}
            />
            <Chat
                agentId={chat.AgentID}
                locale={locale as string}
                name={AIModule.Title}
                assistantAvatar={
                    chat.Avatar || (
                        <ModuleIllustration
                            {...getModuleIllustration(AIModule)}
                        />
                    )
                }
                initialMessage={buildInitialMessage(chat.InitialMessage)}
                initialInput={initialInput as string | undefined}
            />
        </>
    );
};

export const getStaticPaths: GetStaticPaths = () => {
    return {
        paths: [],
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps<Props> = withErrorsGSP<Props>(
    withApollo<Props>(
        withCompany<Props>(
            withProduct<Props>(async ({ apolloClient, product, locale }) => {
                const {
                    data: { modules },
                } =
                    await apolloClient.query<GetAiModuleByProductSlugAndLocaleQuery>(
                        {
                            query: GetAiModuleByProductSlugAndLocaleDocument,
                            variables: {
                                productSlug: product.meta.Slug,
                                locale,
                            },
                        }
                    );

                const AIModule = modules?.[0];

                if (!AIModule) return { notFound: true };

                const { Status, localizations, ...lightAIModule } = AIModule;

                return {
                    props: {
                        AIModule: lightAIModule,
                        hrefLang: buildHreflangArray({
                            entity: AIModule,
                            currentLocale: locale!,
                            basePath: `/${product.meta.Slug}/support`,
                        }),
                        ...(await serverSideTranslations(locale!, [
                            AI,
                            COMMON,
                        ])),
                    },
                    revalidate: SECONDS_PER_DAY,
                };
            })
        )
    )
);

export default SupportPage;
